import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Flex,
  Text,
  Box,
  Link,
  Divider,
  useColorModeValue,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import SearchPager from "../SearchPager";
import { awards } from "../DATABASE/awards";

function matchesQuery(item, query, keys) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return keys.some((k) => String(item?.[k] ?? "").toLowerCase().includes(q));
}

function paginate(list, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    totalPages,
    safePage,
    pageItems: list.slice(start, start + pageSize),
  };
}

/**
 * Header block:
 * Row 1: Title (left) + Back-to-top (right)
 * Row 2: Pager UNDER title (left aligned)
 */
function SectionHeaderBlock({
  title,
  page,
  totalPages,
  onPrev,
  onNext,
  onBackToTop,
  uoRed = "#8F001A",
  uoRedHover = "#6E0014",
}) {
  return (
    <Box mb={2}>
      {/* Row 1 */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Text
          fontSize="lg"
          fontWeight="800"
          textDecoration="underline"
          textUnderlineOffset="4px"
          textDecorationThickness="2px"
          lineHeight="1.2"
        >
          {title}
        </Text>

      </Flex>

      {/* Row 2: pager under title */}
      {totalPages > 1 && (
        <Flex mt={2} align="center" justify="flex-start" wrap="wrap" gap={2}>
          <HStack spacing={2} flexWrap="wrap">
            <IconButton
              aria-label="Previous page"
              icon={<FiChevronLeft />}
              onClick={onPrev}
              isDisabled={page <= 1}
              variant="outline"
              size="sm"
              borderRadius="999px"
            />
            <Text fontSize="sm" minW="88px" textAlign="center">
              Page{" "}
              <Text as="span" fontWeight="700">
                {page}
              </Text>{" "}
              / {totalPages}
            </Text>
            <IconButton
              aria-label="Next page"
              icon={<FiChevronRight />}
              onClick={onNext}
              isDisabled={page >= totalPages}
              variant="outline"
              size="sm"
              borderRadius="999px"
              _hover={{ borderColor: uoRed, color: uoRed }}
            />
          </HStack>
        </Flex>
      )}
    </Box>
  );
}

export default function ResearchAwards() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const textColor = useColorModeValue("gray.700", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const panelBg = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.900");

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
    cursor: "pointer",
  };

  const [query, setQuery] = useState("");
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);

  useEffect(() => setPage(1), [query]);

  const filtered = useMemo(
    () => awards.filter((a) => matchesQuery(a, query, ["title", "date", "text", "link"])),
    [query]
  );

  const pg = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  return (
    <>
      {/* Header */}
      <Container maxW="1100px" py={6}>
        <Text fontSize={{ base: "26px", md: "32px" }} fontWeight="900" textAlign="center">
          Awards, Recognitions and Mentions
        </Text>

        <Text textAlign="center" mt={2} fontSize="lg" color={textColor}>
          <Link href="/" {...linkStyle}>
            Dr. Burak Kantarci
          </Link>
        </Text>

        <Divider mt={6} />
      </Container>

      <Container maxW="1200px" pb={10}>
        {/* Global Search */}
        <Box mt={5}>
          <SearchPager
            mode="search"
            title="Search Awards"
            query={query}
            onQueryChange={setQuery}
            page={1}
            pageSize={PAGE_SIZE}
            totalItems={awards.length}
            filteredItems={filtered.length}
            onPrev={() => {}}
            onNext={() => {}}
          />
        </Box>

        {/* Panel */}
        <Box
          mt={6}
          bg={panelBg}
          border={`1px solid ${borderColor}`}
          borderRadius="18px"
          // ✅ same “grants” style: tight vertical padding but comfy horizontal
          px={{ base: 3, md: 4 }}
          pt={{ base: 3, md: 4 }}
          pb={{ base: 2, md: 3 }}
        >
          <SectionHeaderBlock
            title="Awards"
            page={pg.safePage}
            totalPages={pg.totalPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pg.totalPages, p + 1))}
            onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            uoRed={uoRed}
            uoRedHover={uoRedHover}
          />

          {/* Empty state */}
          {filtered.length === 0 ? (
            <Box mt={3}>
              <Text color="gray.500">No results found for “{query}”.</Text>
            </Box>
          ) : (
            <>
              {pg.pageItems.map((a, idx) => (
                <Box
                  key={`${a.title}-${idx}`}
                  mt={3}
                  // ✅ “absolute” style feel: relative container + accent strip
                  position="relative"
                  bg={cardBg}
                  border={`1px solid ${borderColor}`}
                  borderRadius="16px"
                  p={{ base: 3, md: 4 }}
                  pl={{ base: 4, md: 5 }} // room for accent strip
                  _hover={{
                    borderColor: useColorModeValue("rgba(143,0,26,0.35)", "rgba(143,0,26,0.55)"),
                    boxShadow: useColorModeValue(
                      "0 10px 24px rgba(0,0,0,0.08)",
                      "0 10px 24px rgba(0,0,0,0.35)"
                    ),
                  }}
                  transition="all 160ms ease"
                >
                  {/* Accent strip */}
                  <Box
                    position="absolute"
                    left="0"
                    top="10px"
                    bottom="10px"
                    w="4px"
                    borderRadius="999px"
                    bg={uoRed}
                    opacity={0.95}
                  />

                  {/* Title + Date */}
                  <Flex justify="space-between" align="baseline" wrap="wrap" gap={2}>
                    <Text fontWeight="900" color={textColor} flex="1" minW={{ base: "100%", md: "65%" }}>
                      {a.title}
                    </Text>

                    <Text
                      fontWeight="800"
                      fontStyle="italic"
                      color={useColorModeValue("gray.600", "gray.400")}
                      minW={{ base: "100%", md: "20%" }}
                      textAlign={{ base: "left", md: "right" }}
                    >
                      {a.date}
                    </Text>
                  </Flex>

                  {/* Description */}
                  <Text mt={2} color={textColor} lineHeight="1.75">
                    {a.text}
                  </Text>

                  {/* Link */}
                  {a.link ? (
                    <Box mt={2}>
                      <Link href={a.link} isExternal {...linkStyle}>
                        For more information
                      </Link>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </>
          )}
        </Box>
      </Container>
    </>
  );
}